import { Parameter } from '@shared/types';
import { Button } from '@/components/ui/button';
import { getParameterIcon } from '@/utils/parameterUtils';
import { ParameterLabel } from '@/components/parameter/ParameterLabel';
import { ChevronLeft, ChevronRight } from 'lucide-react';

export function StepperInput({
  param,
  onCommit,
}: {
  param: Parameter;
  onCommit: (value: Parameter['value']) => void;
}) {
  const options = param.options ?? [];
  const loop = param.stepperLoop ?? false;

  // Find the index of the current value in the options list.
  // Compare as strings to handle both number and string value types.
  const currentStr = String(param.value);
  const currentIndex = options.findIndex(
    (opt) => String(opt.value) === currentStr,
  );
  // Fall back to 0 if the current value is not found in the list.
  const safeIndex = currentIndex === -1 ? 0 : currentIndex;

  const atStart = safeIndex === 0;
  const atEnd = safeIndex === options.length - 1;

  const currentLabel =
    currentIndex !== -1 ? options[currentIndex].label : currentStr;

  const goTo = (index: number) => {
    onCommit(options[index].value);
  };

  const handlePrev = () => {
    if (atStart) {
      if (loop) goTo(options.length - 1);
    } else {
      goTo(safeIndex - 1);
    }
  };

  const handleNext = () => {
    if (atEnd) {
      if (loop) goTo(0);
    } else {
      goTo(safeIndex + 1);
    }
  };

  const IconComponent = getParameterIcon(param.name);

  return (
    <div className="grid w-full grid-cols-[96px_1fr] items-center gap-3">
      <ParameterLabel param={param} IconComponent={IconComponent} />
      <div className="flex items-center gap-1">
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 shrink-0"
          onClick={handlePrev}
          disabled={!loop && atStart}
          aria-label="Previous"
        >
          <ChevronLeft className="h-3.5 w-3.5" />
        </Button>
        <span className="min-w-0 flex-1 truncate text-center text-xs font-medium tabular-nums">
          {currentLabel}
        </span>
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 shrink-0"
          onClick={handleNext}
          disabled={!loop && atEnd}
          aria-label="Next"
        >
          <ChevronRight className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
}
